import React from "react";
import { useShoppingCart } from "../context/ShoppingCartContext";
import { CartItemRow } from "./CartItemRow";
import CloseIcon from "@mui/icons-material/Close";
import "./ShoppingCart.css";
import {
  formatDiscountLineAmount,
  pf,
  getPageLocaleUrl,
} from "../utilities/helpers";
import usePageLanguage from "../hooks/usePageLanguage";
import { useAppSelector } from "../../../store/hooks";
import { buildCartDisplaySections } from "../utilities/cartDisplay";
import { cartLineKey } from "../utilities/cartIdentity";
import { sumCartProductsRegular } from "../utilities/cartTotals";
import {
  formatDataLayerCategories,
  formatDataLayerProductName,
} from "../utilities/dataLayerCategories";
import { getCatalogEntry, getEffectivePrice } from "../utilities/productCatalog";

type ShoppingCartProps = {
  isOpen: boolean;
};

const languageTranslations = {
  et: {
    total: "KOKKU",
    cart: "OSTUKORV",
    confirm_order: "VORMISTA TELLIMUS",
    sum: "Summa",
    discount: "Kohandus",
    delivery: "Tarne",
  },
  en: {
    total: "TOTAL",
    cart: "CART",
    confirm_order: "PROCEED TO CHECKOUT",
    sum: "Sum",
    discount: "Adjustment",
    delivery: "Delivery",
  },
};

export default function ShoppingCart({ isOpen }: ShoppingCartProps) {
  const {
    closeCart,
    catalogIndex,
    cartItems,
    increaseCartQuantity,
    order,
    totalCartDiscount,
    totalCartSum,
    pricingNow,
  } = useShoppingCart();

  const cartSidepanel =
    React.useRef() as React.MutableRefObject<HTMLInputElement>;

  const locale = usePageLanguage() || "et";
  const pages = useAppSelector((state) => state.builder.pages.list);

  const checkoutPage = pages.find(
    (page) => page.pageType === "checkout" && page.languageCode === locale
  );

  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  var checkoutHref = checkoutPage
    ? getPageLocaleUrl(checkoutPage, languages)
    : "/et/checkout";
  if (checkoutHref.substring(0, 1) !== "/") {
    checkoutHref = "/" + checkoutHref;
  }

  const deliveryPrice =
    order?.delivery && cartItems.length > 0
      ? Number(order.delivery.taxInclusivePriceAmount ?? 0)
      : 0;
  const cartDisplaySections = buildCartDisplaySections(cartItems, locale);

  const closeCartSidepanel = (e: any) => {
    if (cartSidepanel.current && !cartSidepanel.current.contains(e.target)) {
      closeCart();
    }
  };
  document.addEventListener("mousedown", closeCartSidepanel);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    // Close if clicking on the background overlay or sidePanels div (but not the actual cart content)
    const target = e.target as HTMLElement;
    const isBackgroundClick =
      target.classList.contains("cart-sidePanel") ||
      target.classList.contains("sidePanels");

    if (isOpen && isBackgroundClick) {
      closeCart();
    }
  };

  const lineTotal = (cartItem: (typeof cartItems)[number]) => {
    if (cartItem.invalid || !cartItem.productUuid) {
      return 0;
    }
    const entry = getCatalogEntry(catalogIndex, cartItem.productUuid);
    if (!entry) {
      return 0;
    }
    const { price } = getEffectivePrice(entry, pricingNow);
    return price * cartItem.quantity;
  };

  const handleDatalayerCheckout = () => {
    const productItems = cartItems.map((item) => {
      if (item.invalid || !item.productUuid) {
        return null;
      }
      const entry = getCatalogEntry(catalogIndex, item.productUuid);
      if (!entry) {
        return null;
      }
      const { price, priceRegular } = getEffectivePrice(entry, pricingNow);
      const parent = entry.parent;

      return {
        item_code: item.productUuid,
        item_name: formatDataLayerProductName(parent.translations),
        categories: formatDataLayerCategories(parent.productCategories ?? []),
        price_regular: priceRegular,
        discount_amount: Number(priceRegular - price),
        quantity: item.quantity,
      };
    });

    const productsAmount = cartItems.reduce(
      (total, item) => total + lineTotal(item),
      0
    );

    // @ts-ignore
    window.dataLayer = window.dataLayer || [];
    // @ts-ignore
    window.dataLayer.push({
      event: "begin_checkout",
      ecommerce: {
        total_amount: pf(
          productsAmount + (order?.delivery ? deliveryPrice : 0)
        ),
        total_amount_net: pf(
          productsAmount + (order?.delivery ? deliveryPrice : 0)
        ),
        products_amount: pf(productsAmount),
        products_amount_net: pf(productsAmount),
        shipping_amount: order?.delivery ? deliveryPrice : 0,
        shipping_amount_net: order?.delivery ? deliveryPrice : 0,
        items: [...productItems],
      },
    });
  };

  return (
    <div
      ref={cartSidepanel}
      className="cart-sidePanel"
      onClick={handleBackgroundClick}
    >
      <div className="sidePanels" style={{ backgroundColor: "#fff" }}>
        <div
          id="cartSidepanel"
          className={`sidePanel${isOpen ? " visible" : ""}`}
        >
          <div style={{ fontSize: "21px", fontWeight: "400" }}>
            <span
              className="panel-title inline-block"
              style={{
                fontWeight: "600",
                fontSize: "18px",
                fontFamily: "Roboto, sans-serif",
                lineHeight: "21.6px",
              }}
            >
              {languageTranslations[locale]?.cart}
            </span>
            <CloseIcon
              className="btn-pure clickable2 padding-5 pull-right"
              onClick={() => closeCart()}
            />
          </div>
          <div
            className=""
            style={{ height: "calc(100% - 59px)", overflowX: "auto" }}
          >
            <div
              className="table-container padding-top-20"
              style={{
                overflowX: "auto",
                maxHeight: "calc(100% - 200px)",
                minHeight: "150px !important",
              }}
            >
              <div className="table tablerow-no-border cartTable">
                <div className="cart-line-items-live">
                  {cartDisplaySections.map((section) => (
                    <React.Fragment key={section.key}>
                      {(section.label || section.showGroupRow) && (
                        <div className="cartEventGroupRow">
                          {section.label}
                        </div>
                      )}
                      {section.items.map((item) => (
                        <CartItemRow
                          key={cartLineKey(item)}
                          {...item}
                        />
                      ))}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
            <div className="cart-summary">
              {order?.delivery?.uuid || totalCartDiscount ? (
                <div className="cart-summary-row">
                  <span className="cart-summary-row__label">
                    {locale
                      ? languageTranslations[locale]?.sum
                      : languageTranslations.et.sum}
                    :
                  </span>
                  <span className="cart-summary-row__amount">
                    <span className="cartTotal">
                      {pf(
                        sumCartProductsRegular(
                          cartItems.map((item) => ({
                            productUuid: item.productUuid,
                            quantity: item.quantity,
                            invalid: item.invalid,
                          })),
                          catalogIndex,
                          pricingNow
                        )
                      )}
                    </span>{" "}
                    &euro;
                  </span>
                </div>
              ) : null}

              {totalCartDiscount !== 0 ? (
                <div className="cart-summary-row cart-summary-row--discount discount-price-color">
                  <span className="cart-summary-row__label">
                    {languageTranslations[locale]?.discount}:
                  </span>
                  <span className="cart-summary-row__amount">
                    <span className="cartTotal">
                      {formatDiscountLineAmount(totalCartDiscount)}
                    </span>{" "}
                    &euro;
                  </span>
                </div>
              ) : null}
              {order?.delivery?.uuid && cartItems.length > 0 ? (
                <div className="cart-summary-row">
                  <span className="cart-summary-row__label">
                    {languageTranslations[locale]?.delivery}:
                  </span>
                  <span className="cart-summary-row__amount">
                    <span className="cartTotal">{pf(deliveryPrice)}</span>{" "}
                    &euro;
                  </span>
                </div>
              ) : null}
              <div className="cart-summary-row cart-summary-row--total">
                <span className="cart-summary-row__label">
                  {languageTranslations[locale]?.total}:
                </span>
                <span className="cart-summary-row__amount">
                  <span className="cartTotal">
                    {pf(totalCartSum + deliveryPrice)}
                  </span>{" "}
                  &euro;
                </span>
              </div>
            </div>
            <div className="padding-top-20">
              <a
                onClick={() => {
                  handleDatalayerCheckout();
                }}
                className="btn btn-primary shop-controlled-border-radius confirm-order-button"
                href={checkoutHref}
                style={{ width: "100%", color: "#fff" }}
              >
                {languageTranslations[locale]?.confirm_order}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
